import DOMPurify from 'dompurify'
import formatJobDescription from './FormatJobDescription'

// ---------- helpers ----------
const toValidDate = (input) => {
  if (!input) return null
  try {
    const d = input instanceof Date ? new Date(input.getTime()) : new Date(input)
    return isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

// Always return YYYY-MM-DD (UTC) or '' — great for .split('-')
const formatDateISOYYYYMMDD = (date) => {
  if (!date) return ''
  try {
    return new Date(date.getTime()).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

const safeCurrency = (code) => {
  const up = String(code || '').toUpperCase()
  return /^[A-Z]{3}$/.test(up) ? up : 'USD'
}

const toMoney = (value, currencyCode) => {
  if (value === null || value === undefined) return 'N/A'
  const num = Number(value)
  if (!isFinite(num)) return 'N/A'
  try {
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: safeCurrency(currencyCode),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  } catch {
    return `${num}`
  }
}

// ---------- JOBS ----------
export const transformJobs = (jobs) =>
  (Array.isArray(jobs) ? jobs : []).map((job) => {
    const parsedDate = toValidDate(job?.posted_at)
    const dateISO = formatDateISOYYYYMMDD(parsedDate) // ← ensure string for FeedCard.split

    const currency = safeCurrency(job?.currency)
    const minValue = toMoney(job?.salary_min, currency)
    const maxValue = toMoney(job?.salary_max, currency)
    const salaryInterval = '' // add 'year'/'month' if you have it

    const location = job?.location || 'Unknown Location'
    const descriptionHtml = formatJobDescription(job?.description || '')
    const formattedDescription =
      descriptionHtml.length > 283 ? descriptionHtml.slice(0, 283) + '...' : descriptionHtml

    const details = (
      <>
        <h4 style={{ margin: 0, padding: 0 }}>{job?.company_name || ''}</h4>
        <span>{location}</span>
        <br />
        {salaryInterval ? (
          <span>
            {minValue} - {maxValue} / {salaryInterval}
          </span>
        ) : (
          <span>
            {minValue} - {maxValue}
          </span>
        )}
        <hr />
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedDescription) }} />
      </>
    )

    return {
      id: job?.id,
      category: 'Jobs',
      title: job?.title || '(untitled)',
      company: job?.company_name || '',
      // IMPORTANT: string for FeedCard's .split('-')
      datePosted: dateISO,
      summary: job?.url ? `Visit ${job.url}` : '',
      description: details,
      url: job?.url || '',
      seen: false,
    }
  })

// ---------- COURSES ----------
export const transformCourses = (data) =>
  (Array.isArray(data) ? data : []).map((course) => ({
    id: course?.id,
    category: 'Courses',
    title: course?.title || '(untitled)',
    company: course?.provider || '',
    datePosted: '', // not used for grouping
    summary: course?.provider ? `Course by ${course.provider}` : '',
    description: course?.description || '',
    url: course?.url || '',
    seen: false,
  }))

// ---------- EVENTS ----------
export const transformEvents = (data = []) =>
  (Array.isArray(data) ? data : []).map((event) => {
    const start = toValidDate(event?.start_at)
    const end = toValidDate(event?.end_at)

    const details = (
      <>
        <span>
          {/* Keep the pretty range inside the description */}
          {start
            ? start.toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : ''}
          {end
            ? ` – ${end.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}`
            : ''}
        </span>
      </>
    )

    return {
      id: event?.id,
      category: 'Events',
      title: event?.title || '(untitled)',
      // IMPORTANT: string for FeedCard's .split('-')
      datePosted: formatDateISOYYYYMMDD(start),
      location: event?.location || '',
      description: details,
      url: event?.url || '',
      seen: false,
    }
  })

// ---------- NEWS ----------
export const transformNews = (data) =>
  (Array.isArray(data) ? data : []).map((newsItem) => {
    // schema column name is published_at
    const published = toValidDate(newsItem?.published_at)

    return {
      id: newsItem?.id,
      category: 'News',
      title: newsItem?.title || '(untitled)',
      // IMPORTANT: string for FeedCard's .split('-')
      datePosted: formatDateISOYYYYMMDD(published),
      source: newsItem?.publisher || '',
      description: newsItem?.summary || '',
      url: newsItem?.url || '',
      seen: false,
    }
  })
